import { Head, Link, router } from "@inertiajs/react"
import { Box, Button, Paper, Stack, Typography } from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayer from "../../../components/AudioPlayer"

type Translation = {
  id: number
  language: string
  text: string
  hint: string | null
  literal?: string | null
  position: number
}

type Audio = {
  url: string
  language: string
  ai_model: string
  voice: string
}

type Sentence = {
  id: number
  kanji: string | null
  kana: string | null
  romaji: string | null
  translations: Translation[]
  audio: Audio | null
  topic_ids: number[]
  words: { id: number; kanji: string; kana: string; translations: Translation[] }[]
}

const LANGUAGE_LABELS: Record<string, string> = { en: "English", de: "Deutsch" }

function TranslationsList({ translations }: { translations: Translation[] }) {
  if (translations.length === 0) return <Typography>—</Typography>
  const byLang: Record<string, Translation[]> = {}
  translations.forEach((t) => {
    byLang[t.language] ??= []
    byLang[t.language].push(t)
  })
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {Object.keys(byLang).map((lang) => (
        <Box key={lang} data-testid={`translations-${lang}`}>
          <Typography variant="subtitle2">{LANGUAGE_LABELS[lang] ?? lang}</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {byLang[lang].map((t) => (
              <Box key={t.id}>
                <Typography>{t.text}</Typography>
                {t.hint && (
                  <Typography variant="body2" color="text.secondary">Hint: {t.hint}</Typography>
                )}
                {t.literal && (
                  <Typography variant="body2" color="text.secondary">Literal: {t.literal}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default function ShowSentence({ sentence }: { sentence: Sentence }) {
  const remove = () => {
    if (confirm("Delete this sentence?")) router.delete(`/admin/sentences/${sentence.id}`)
  }

  return (
    <AdminLayout title={sentence.kanji || sentence.kana || "Sentence"}>
      <Head title="Sentence" />
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">{sentence.kanji || sentence.kana}</Typography>
        <Stack direction="row" gap={1}>
          <Button component={Link} href={`/admin/sentences/${sentence.id}/edit`}>Edit</Button>
          <Button color="error" onClick={remove}>Delete</Button>
        </Stack>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, alignItems: "start" }}>
          <Typography color="text.secondary">Kanji</Typography><Typography>{sentence.kanji || "—"}</Typography>
          <Typography color="text.secondary">Kana</Typography><Typography>{sentence.kana || "—"}</Typography>
          <Typography color="text.secondary">Romaji</Typography><Typography>{sentence.romaji || "—"}</Typography>
          <Typography color="text.secondary">Translations</Typography>
          <TranslationsList translations={sentence.translations} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Audio</Typography>
          <AudioPlayer src={sentence.audio?.url ?? null} />
          {sentence.audio && (
            <Typography variant="caption" color="text.secondary">
              {sentence.audio.language} · {sentence.audio.ai_model} · {sentence.audio.voice}
            </Typography>
          )}
        </Box>
      </Paper>
      <Typography variant="h6" sx={{ mt: 3 }}>Words ({sentence.words.length})</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {sentence.words.map((w) => {
          const en = w.translations.find((t) => t.language === "en")?.text
          return (
            <Link key={w.id} href={`/admin/words/${w.id}`}>
              {w.kanji || w.kana} — {en || "—"}
            </Link>
          )
        })}
      </Box>
    </AdminLayout>
  )
}
